import type { ICache } from './ICache'

interface CachedData<T = any> {
    key: string
    timestamp: number
    data: T
}

/**
 * Cache com persistência em IndexedDB e TTL configurável por entrada.
 * Use {@link LocalCache.getInstance} para obter ou criar uma instância por `storeName`.
 * @hideconstructor
 */
export class LocalCache implements ICache {
    private static readonly instances: Map<string, LocalCache> = new Map()
    private dbPromise: Promise<IDBDatabase> | null = null

    private constructor(
        private readonly storeName: string,
        private readonly cacheExpirationMs: number
    ) {}

    /**
     * Retorna a instância de LocalCache para o `storeName` informado, criando-a se necessário.
     * @param storeName Nome do object store no IndexedDB (identificador único do cache)
     * @param cacheExpirationMs Tempo de expiração em milissegundos
     */
    public static getInstance(storeName: string, cacheExpirationMs: number): LocalCache {
        if (!LocalCache.instances.has(storeName)) {
            LocalCache.instances.set(storeName, new LocalCache(storeName, cacheExpirationMs))
        }
        return LocalCache.instances.get(storeName)!
    }

    private promisifyRequest<T>(request: IDBRequest<T>): Promise<T> {
        return new Promise((resolve, reject) => {
            request.onsuccess = () => resolve(request.result)
            request.onerror = () => reject(request.error)
        })
    }

    private async openDatabase(): Promise<IDBDatabase> {
        if (this.dbPromise) return this.dbPromise
        // Each storeName gets its own database — avoids DB_VERSION collision
        const dbName = `jsegd_cache_${this.storeName}`
        this.dbPromise = new Promise((resolve, reject) => {
            const request = indexedDB.open(dbName, 1)
            request.onupgradeneeded = () => {
                const db = request.result
                if (!db.objectStoreNames.contains(this.storeName)) {
                    db.createObjectStore(this.storeName, { keyPath: 'key' })
                }
            }
            request.onsuccess = () => resolve(request.result)
            request.onerror = () => {
                reject(request.error)
            }
        })
        return this.dbPromise
    }

    /**
     * Retorna o valor em cache para a chave informada, ou `null` se não encontrado ou expirado.
     */
    public async get<T = any>(key: string): Promise<T | null> {
        try {
            const db = await this.openDatabase()
            const transaction = db.transaction(this.storeName, 'readonly')
            const store = transaction.objectStore(this.storeName)
            const result = (await this.promisifyRequest(store.get(key))) as CachedData<T> | undefined
            if (!result) return null
            if (Date.now() - result.timestamp > this.cacheExpirationMs) {
                await this.remove(key).catch(() => {})
                return null
            }
            return result.data
        } catch (error) {
            return null
        }
    }

    /**
     * Armazena um valor no cache sob a chave informada.
     */
    public async set<T = any>(key: string, data: T): Promise<void> {
        try {
            const db = await this.openDatabase()
            const transaction = db.transaction(this.storeName, 'readwrite')
            const store = transaction.objectStore(this.storeName)
            const payload: CachedData<T> = { key, timestamp: Date.now(), data }
            await this.promisifyRequest(store.put(payload))
            await new Promise<void>((resolve, reject) => {
                transaction.oncomplete = () => resolve()
                transaction.onerror = () => reject(transaction.error)
            })
        } catch (error) {
            throw error
        }
    }

    /**
     * Remove a entrada em cache para a chave informada.
     */
    public async remove(key: string): Promise<void> {
        try {
            const db = await this.openDatabase()
            const transaction = db.transaction(this.storeName, 'readwrite')
            const store = transaction.objectStore(this.storeName)
            await this.promisifyRequest(store.delete(key))
            await new Promise<void>((resolve, reject) => {
                transaction.oncomplete = () => resolve()
                transaction.onerror = () => reject(transaction.error)
            })
        } catch (error) {
            throw error
        }
    }

    /**
     * Remove todas as entradas do cache.
     */
    public async clear(): Promise<void> {
        try {
            const db = await this.openDatabase()
            const transaction = db.transaction(this.storeName, 'readwrite')
            const store = transaction.objectStore(this.storeName)
            await this.promisifyRequest(store.clear())
            await new Promise<void>((resolve, reject) => {
                transaction.oncomplete = () => resolve()
                transaction.onerror = () => reject(transaction.error)
            })
        } catch (error) {
            throw error
        }
    }

    /**
     * Remove todas as entradas expiradas e retorna a quantidade removida.
     * @returns Número de entradas removidas
     */
    public async purgeExpired(): Promise<number> {
        try {
            const db = await this.openDatabase()
            const transaction = db.transaction(this.storeName, 'readwrite')
            const store = transaction.objectStore(this.storeName)
            const all = (await this.promisifyRequest(store.getAll())) as CachedData[]
            const now = Date.now()
            const expired = all.filter((entry) => now - entry.timestamp > this.cacheExpirationMs)
            for (const entry of expired) {
                await this.promisifyRequest(store.delete(entry.key))
            }
            await new Promise<void>((resolve, reject) => {
                transaction.oncomplete = () => resolve()
                transaction.onerror = () => reject(transaction.error)
            })
            return expired.length
        } catch (error) {
            return 0
        }
    }
}
